import type ApplicationInstance from '@ember/application/instance';
import { registerDestructor } from '@ember/destroyable';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { runTask } from 'ember-lifeline';
import Micromodal from 'micromodal';

interface Sig {
  Args: {
    targetModal: Parameters<(typeof Micromodal)['show']>[0];
    config?: Parameters<(typeof Micromodal)['show']>[1];
    onOverlayClick?: () => void;
    labelElement: string;
  };
  Blocks: { default: [] };
}

export default class MicromodalContentComponent extends Component<Sig> {
  @service declare router: RouterService;

  modal: HTMLElement;
  overlay: Element;
  dialog: Element;

  constructor(owner: ApplicationInstance, args: Sig['Args']) {
    super(owner, args);

    // Get the DOM elements
    const modal = document.getElementById(args.targetModal);
    const overlay = modal?.firstElementChild;
    const dialog = modal?.querySelector('[role="dialog"]');

    // Assert the modal is properly set in the DOM
    if (!modal || !overlay || !dialog)
      throw `You are trying to set the content of a modal that is not properly set in the DOM.
      Make sure element with id '${args.targetModal}' exists and has an overlay and a dialog.`;

    // Keep refs to the DOM elements
    this.modal = modal;
    this.overlay = overlay;
    this.dialog = dialog;

    // Register the "click" event on the overlay and set aria-labelledBy on dialog
    this.overlay.addEventListener('click', this.onOverlayClick);
    this.dialog.setAttribute('aria-labelledby', this.args.labelElement);

    runTask(this, () => {
      Micromodal.show(this.modal.id, args.config);
    });

    // Make sure we close the modal when MicromodalContentComponent is removed from the DOM
    registerDestructor(this, () => {
      this.overlay.removeEventListener('click', this.onOverlayClick);
      this.dialog.setAttribute('aria-labelledby', '');

      /*
        If the modal element is not in the DOM anymore, do nothing.

        This might happen in tests when the test ends with an open modal
        The DOM will then be reset, the modal element removed from the DOM
        and this method called afterwards

        Trying to close the modal in that case would error
      */
      if (!this.modal.isConnected) return;

      // Only close the modal if it is empty.
      // If is is not empty, this means that the content has been set by another component
      // before this destructor was called, and we do not want to close the modal
      //
      // ex: `route1` and `route2` use this MicromodalContentComponent to display different content in the modal
      // In route1, you have a <LinkTo @route="route2">
      // When the transition happens:
      // - the content of route2 replaces the content of route1 in the modal
      // - registerDestructor is called
      //
      // Checking `this.isEmpty` makes sure the modal will stay visible with the content of route2
      if (this.isEmpty) this.closeModal();
    });
  }

  get isEmpty() {
    return this.dialog.childElementCount == 0;
  }

  onOverlayClick = (e: Event) => {
    // Do nothing unless the overlay itself was clicked
    if (e.target != this.overlay) return;
    // TODO: pre-existing, see the eslint-plugin-ember migration ticket
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.args.onOverlayClick ? this.args.onOverlayClick() : this.closeModal();
  };

  closeModal = () => {
    Micromodal.close(this.modal.id);
  };

  <template>
    {{#in-element this.dialog}}
      {{yield}}
    {{/in-element}}
  </template>
}
